import emailjs from '@emailjs/browser';

// EmailJS Configuration
// Get these values from your EmailJS dashboard
const EMAILJS_CONFIG = {
    publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
    serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID,
    recipientEmail: process.env.REACT_APP_RECIPIENT_EMAIL || 'tech@adjactive.com',
    templates: {
        auditRequest: process.env.REACT_APP_EMAILJS_AUDIT_TEMPLATE,
        frameworkDownload: process.env.REACT_APP_EMAILJS_FRAMEWORK_TEMPLATE,
        researcherContact: process.env.REACT_APP_EMAILJS_RESEARCHER_TEMPLATE,
    },
};

// Initialize EmailJS with your public key - Using new format for v4
const initEmailJS = () => {
    if (EMAILJS_CONFIG.publicKey) {
        emailjs.init({
            publicKey: EMAILJS_CONFIG.publicKey,
            blockHeadless: true,
        });
    }
};

// Initialize on module load
initEmailJS();

/**
 * Send AI Audit Request Email
 */
export const sendAuditRequest = async (formData) => {
    try {
        if (!EMAILJS_CONFIG.publicKey || !EMAILJS_CONFIG.serviceId || !EMAILJS_CONFIG.templates.auditRequest) {
            throw new Error('Email service is currently unavailable. Please contact us directly.');
        }

        const templateParams = {
            form_type: 'AI Audit Request',
            from_name: formData.name?.trim(),
            from_email: formData.email?.trim(),
            company: formData.company?.trim(),
            role: formData.role?.trim(),
            use_case: formData.useCase?.trim(),
            message: formData.useCase?.trim(),
            to_email: EMAILJS_CONFIG.recipientEmail,
            timestamp: new Date().toLocaleString(),
        };

        const response = await emailjs.send(
            EMAILJS_CONFIG.serviceId,
            EMAILJS_CONFIG.templates.auditRequest,
            templateParams,
            EMAILJS_CONFIG.publicKey
        );

        return { success: true, data: response };
    } catch (error) {
        console.error('Email Service Error:', error);
        throw new Error('Failed to send request. Please try again later.');
    }
};

/**
 * Send Framework Download Request Email
 */
export const sendFrameworkRequest = async (email) => {
    try {
        if (!EMAILJS_CONFIG.publicKey || !EMAILJS_CONFIG.serviceId || !EMAILJS_CONFIG.templates.frameworkDownload) {
            throw new Error('EmailJS configuration missing');
        }

        const templateParams = {
            form_type: 'Framework Download',
            from_name: 'N/A',
            from_email: email?.trim(),
            company: 'N/A',
            role: 'N/A',
            use_case: 'N/A',
            message: 'Framework download requested',
            to_email: EMAILJS_CONFIG.recipientEmail,
            timestamp: new Date().toLocaleString(),
        };

        const response = await emailjs.send(
            EMAILJS_CONFIG.serviceId,
            EMAILJS_CONFIG.templates.frameworkDownload,
            templateParams,
            EMAILJS_CONFIG.publicKey
        );

        return { success: true, data: response };
    } catch (error) {
        console.error('Email Service Error:', error);
        throw new Error('Failed to send request. Please try again later.');
    }
};

/**
 * Send Researcher Contact Email
 */
export const sendResearcherContact = async (formData) => {
    try {
        if (!EMAILJS_CONFIG.publicKey || !EMAILJS_CONFIG.serviceId || !EMAILJS_CONFIG.templates.researcherContact) {
            throw new Error('EmailJS configuration missing');
        }

        const templateParams = {
            form_type: 'Talk to a Researcher',
            from_name: sanitizeInput(formData.name?.trim()),
            from_email: formData.email?.trim(),
            company: sanitizeInput(formData.company?.trim()),
            role: sanitizeInput(formData.role?.trim()),
            use_case: 'N/A',
            message: sanitizeInput(formData.message?.trim()) || 'No message provided',
            to_email: EMAILJS_CONFIG.recipientEmail,
            timestamp: new Date().toLocaleString(),
        };

        const response = await emailjs.send(
            EMAILJS_CONFIG.serviceId,
            EMAILJS_CONFIG.templates.researcherContact,
            templateParams,
            EMAILJS_CONFIG.publicKey
        );

        return { success: true, data: response };
    } catch (error) {
        console.error('Email Service Error:', error);
        throw new Error('Failed to send message. Please try again later.');
    }
};

/**
 * Check if EmailJS is properly configured
 * @returns {boolean} True if all required config is present
 */
export const isEmailJSConfigured = () => {
    return !!(
        EMAILJS_CONFIG.publicKey &&
        EMAILJS_CONFIG.serviceId &&
        (EMAILJS_CONFIG.templates.auditRequest || EMAILJS_CONFIG.templates.frameworkDownload)
    );
};

// EmailJS is ready for use. Configuration is handled through environment variables.

